import { useEffect } from 'react';
import { useUpdates } from 'expo-updates';

import { type ActorRefFrom } from 'xstate';

import { type otaMachine } from './ota-machine';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A hook that observes and manages an OTA machine's state based on certain
 * parameters and conditions.
 * It interacts with an actor reference to control the state machine and reacts
 * to updates and enabled status.
 *
 * - It uses multiple effects to manage the lifecycle of an OTA state machine.
 * - It dispatches `enable` / `disable` to the machine based on the `isEnabled`
 *   parameter.
 * - It also notifies the machine of any initial updates.
 */
export const useOtaMachineObserver = (params: UseOtaMachineObserverParams) => {
  const { actorRef, isEnabled, isReady } = params;

  const { downloadedUpdate } = useUpdates();

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    // Hold off until the consumer signals that `isEnabled` is final — otherwise
    // a default `false` would prematurely send `disable` and exit the launch
    // gate.
    if (isReady === false) return;

    const eventType = isEnabled ? 'enable' : 'disable';

    actorRef.send({ type: eventType });
  }, [isEnabled, isReady, actorRef]);

  useEffect(() => {
    if (!isEnabled || !downloadedUpdate) return;

    actorRef.send({ type: 'notify-about-update' });
  }, [downloadedUpdate, isEnabled, actorRef]);
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseOtaMachineObserverParams = {
  isEnabled: boolean;
  isReady?: boolean;
  actorRef: ActorRefFrom<typeof otaMachine>;
};
